/* eslint-disable */
// ============================================================
//  native-google.js — Connexion Google DANS l'app installee.
//
//  Le probleme qu'on repare : dans l'app Android, `signInWithOAuth`
//  ouvre CHROME, parce que Google interdit son ecran de connexion a
//  l'interieur d'une WebView embarquee. La session est alors creee
//  dans Chrome, qui est un conteneur separe : l'app ne la voit jamais
//  et retraite la personne en invitee a chaque ouverture.
//
//  La solution : demander a Android son propre selecteur de compte
//  (Credential Manager, via le plugin), recuperer le jeton d'identite
//  qu'il delivre, et le donner directement a Supabase. Aucun
//  navigateur n'est ouvert, la session reste dans l'app.
//
//  ⚠️ NO-OP total sur le web et sur tout build natif ou le plugin est
//  absent : on retombe alors sur le flux OAuth d'origine.
// ============================================================
import { supabase, trackSignupEvent } from "./supabase";

// Identifiant WEB du projet Google `horseconnex` — le meme que celui
// configure dans Supabase (Authentication -> Providers -> Google).
// ⚠️ C'est bien le client WEB qu'Android attend comme `webClientId` :
// c'est lui qui devient l'audience du jeton, donc celui que Supabase
// verifie. Les deux clients ANDROID ne sont jamais cites ici — leur
// seul role est d'exister dans le projet pour que Google reconnaisse
// l'app a sa signature.
const WEB_CLIENT_ID = "157965617368-unh8muqgk5dlvlj9qaigltqfgfkaks2r.apps.googleusercontent.com";

let initialized = false;

function estAppNative() {
  if (typeof window === "undefined") return false;
  const Cap = window.Capacitor;
  return !!(Cap && typeof Cap.isNativePlatform === "function" && Cap.isNativePlatform());
}

function getPlugin() {
  if (!estAppNative()) return null;
  const Cap = window.Capacitor;
  return (Cap.Plugins && Cap.Plugins.SocialLogin) || null;
}

export function hasNativeGoogle() {
  return !!getPlugin();
}

// Releve anonyme du motif d'echec du chemin natif.
// ⚠️ Sans lui on ne peut PAS savoir pourquoi Chrome s'ouvre encore : le
// message d'erreur ne vit que dans la console du telephone, hors d'atteinte.
// Aucune donnee personnelle : uniquement un code technique, et toute adresse
// qui traverserait un message d'erreur est remplacee avant enregistrement.
function noterEchec(code) {
  try {
    const propre = String(code || "inconnu")
      .replace(/[\w.+-]+@[\w.-]+/g, "<email>")
      .replace(/\s+/g, " ")
      .slice(0, 90);
    trackSignupEvent("gauth_" + propre, null);
  } catch (e) {}
}

// L'utilisateur a referme le selecteur de comptes : ce n'est pas une
// panne, c'est un choix. On ne doit surtout pas enchainer sur Chrome.
function isCancel(e) {
  const m = ((e && (e.message || e.errorMessage)) || "").toLowerCase();
  return m.includes("cancel") || m.includes("annul") || m.includes("dismiss");
}

async function loginNative(SocialLogin) {
  // Capacitor genere les methodes JS a partir des annotations Java. Si R8 les
  // avait retirees, l'objet existerait sans ses methodes — cas indiscernable
  // d'un plugin absent sans ce controle explicite.
  if (typeof SocialLogin.initialize !== "function") throw new Error("sansMethode:initialize");
  if (typeof SocialLogin.login !== "function") throw new Error("sansMethode:login");

  if (!initialized) {
    try {
      await SocialLogin.initialize({ google: { webClientId: WEB_CLIENT_ID } });
    } catch (e) {
      throw new Error("initialize:" + ((e && e.message) || e));
    }
    initialized = true;
  }

  let res;
  try {
    // ⚠️ NE JAMAIS passer `scopes` ici. Le plugin ajoute deja de lui-meme
    // userinfo.email, userinfo.profile et openid -- soit exactement ce dont on
    // a besoin. Et surtout : la moindre liste de scopes le fait refuser avec
    // « You CANNOT use scopes without modifying the main activity », car cela
    // exige de faire heriter MainActivity d'une classe du plugin. C'est ce qui
    // faisait echouer le chemin natif et rouvrir Chrome (releve du 11 sept.).
    res = await SocialLogin.login({ provider: "google", options: {} });
  } catch (e) {
    if (isCancel(e)) throw e;
    throw new Error("login:" + ((e && e.message) || e));
  }

  const idToken = res && res.result && res.result.idToken;
  if (!idToken) throw new Error("sansJeton:" + JSON.stringify(res || {}).slice(0, 50));

  const { error } = await supabase.auth.signInWithIdToken({ provider: "google", token: idToken });
  if (error) throw new Error("supabase:" + (error.message || ""));
}

async function loginWeb() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: window.location.origin },
  });
  if (error) throw error;
}

// Point d'entree unique des boutons « Se connecter avec Google ».
// Renvoie true si l'utilisateur a simplement annule (rien a signaler).
export async function signInWithGoogle() {
  const SocialLogin = getPlugin();

  if (!SocialLogin) {
    if (estAppNative()) {
      // Dans l'app mais sans le plugin : on note quels plugins SONT exposes.
      // Si PushNotifications est la et pas SocialLogin, c'est le build qui
      // ne l'embarque pas ; s'il n'y a rien, c'est le pont qui n'a pas demarre.
      let noms = "?";
      try { noms = Object.keys((window.Capacitor && window.Capacitor.Plugins) || {}).join("+"); } catch (e) {}
      noterEchec("absent:" + noms);
    }
    await loginWeb();
    return false;
  }

  try {
    await loginNative(SocialLogin);
    return false;
  } catch (e) {
    if (isCancel(e)) return true;
    // Panne du chemin natif : on ne laisse personne enferme dehors,
    // on repasse par le navigateur (comportement d'avant).
    console.error("[google] connexion native en echec, repli navigateur:", e);
    noterEchec((e && e.message) || e);
    await loginWeb();
    return false;
  }
}
